import * as React from "react";
const {useCallback, useEffect, useMemo, useState} = React;

export type TemplatePair = any; // TODO: replace with real type

export type Filter = BasicFilter | DiscreteRangeFilter;
export type BasicFilter = {
    label   : string,
    type    : "basic",
    options : BasicFilterOption[],
};
export type BasicFilterOption = {
    label       : string,
    matchers    : FilterOptionMatcher[],
    typeData    : null | {
        appearance  ?: `${BasicFilterOptionAppearanceType}:${string}`,
    },
};
export type BasicFilterOptionAppearanceType = "color" | "layout";

export type DiscreteRangeFilter = {
    label   : string,
    type    : "discreteRange",
    options : DiscreteRangeFilterOption[],
};
export type DiscreteRangeFilterOption = {
    label       : string,
    matchers    : FilterOptionMatcher[],
    typeData    : {rangeValue: any},
};

export type FilterOptionMatcher = (
    | FieldValueFilterOptionMatcher
    | MappedValueFilterOptionMatcher
    | TemplateKeywordsFilterOptionMatcher
    | VariationIdsFilterOptionMatcher
);
export type FieldValueFilterOptionMatcher = {
    type: "fieldValue",
    config: {field: string, value: any},
};
export type MappedValueFilterOptionMatcher = {
    type: "mappedValue",
    config: {mapName: string, expectedValue: any},
};
export type TemplateKeywordsFilterOptionMatcher = {
    type: "templateKeywords",
    config: string[],
};
export type VariationIdsFilterOptionMatcher = {
    type: "variationIds",
    config: string[],
};

export type FilterConfig = BasicFilterConfig | DiscreteRangeFilterConfig;
export type BasicFilterConfig = {type: "basic", activeOptions: string[]};
export type DiscreteRangeFilterConfig = {type: "discreteRange", indexMin: any, indexMax: any};

export const useSearchFilters = (query: string) => {
    const [filters, setFilters] = useState<Filter[]>([]);
    const [configs, setConfigs] = useState<Record<string, FilterConfig>>({});
    const filtersConfigsTuples  = useMemo(
        () => filters.map(f => [f, configs[f.label] ?? null] as const),
        [filters, configs]
    );

    // Fetch
    useEffect(() => {
        if (query) {
            fetch(`/next-api/omniSearch/search/filters?q=${query}`)
            .then(res => res.json() as Promise<Filter[]>)
            .then(filters =>
            {
                filters.sort((a, b) =>
                {
                    if (a.label === "Categories") return -1;
                    if (b.label === "Categories") return 1;

                    if (a.label === "Products") return -1;
                    if (b.label === "Products") return 1;

                    return a.label.localeCompare(b.label);
                });
                setFilters(filters);
            })
        }
    }, [query]);

    // load URL params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlConfigs: Record<string, FilterConfig> = {};

        filters.forEach(filter => {
            const paramValue = params.get(`filter_${labelAsQueryValue(filter.label)}`);
            if (paramValue) {
                if (filter.type === "basic") {
                    const queryLabels = paramValue.split(",");
                    urlConfigs[filter.label] = {
                        type: "basic",
                        activeOptions: (
                            filter.options
                            .filter(({label}) => queryLabels.includes(labelAsQueryValue(label)))
                            .map(({label}) => label)
                        )
                    };
                } else if (filter.type === "discreteRange") {
                    const [indexMin, indexMax] = paramValue.split(",").map(Number);
                    urlConfigs[filter.label] = {
                        type: "discreteRange",
                        indexMin,
                        indexMax,
                    };
                }
            }
        });

        setConfigs(urlConfigs);
    }, [filters]);

    // save URL params
	useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        Object.entries(configs).forEach(([label, config]) => {
            const queryParam = `filter_${labelAsQueryValue(label)}`;
            if (config.type === "basic" && config.activeOptions.length > 0) {
                params.set(queryParam, config.activeOptions.map(labelAsQueryValue).join(','));
            } else if (config?.type === "discreteRange") {
                params.set(queryParam, `${config.indexMin},${config.indexMax}`);
            } else {
                params.delete(queryParam);
            }
        });

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
    }, [configs]);

    const mapResultSet = useCallback(resultSetMap(filtersConfigsTuples), [filtersConfigsTuples]);

    const getOptionCounts = useCallback((filterLabel: string, resultSet: TemplatePair[]) => {
        const filter = filters.find(f => f.label === filterLabel);
        if (!filter || filter.type !== "basic") return {};

        const otherFiltersConfigsTuples = filtersConfigsTuples.filter(([f]) => f.label !== filterLabel);

        const counts: Record<string, number> = {};

        filter.options.forEach(option => {
            const optionConfig: BasicFilterConfig = {
                type: "basic",
                activeOptions: [option.label]
            };
            const mapResultSet = resultSetMap([...otherFiltersConfigsTuples, [filter, optionConfig]]);
            counts[option.label] = mapResultSet(resultSet).length;
        });

        return counts;
    }, [filtersConfigsTuples]);

    return {
        filters,
        getOptionCounts,
        mapResultSet,
        filterConfigs: configs,
        clearFilterConfigs()
        {
            setConfigs({});
        },
        setFilterConfig(filterLabel: string, config: FilterConfig)
        {
            setConfigs(configs => ({...configs, [filterLabel]: config}));
        },
    };
};

// Map repository for mappedValue matchers
const MAPPED_VALUE_MATCHER_MAPS: Record<string, (row: TemplatePair) => any> = {
    templatePairAsLayout: (row) => {
        const notApplicable = !row.finished_height || !row.finished_width;
        if (notApplicable) {
            return "";
        } else if (row.finished_height === row.finished_width) {
            return "square";
        } else if (row.finished_height < row.finished_width) {
            return "horizontal";
        } else {
            return "vertical";
        }
    },

    templatePairAsSize: (row) => {
        const notApplicable = !row.finished_height || !row.finished_width;
        return notApplicable ? "" : `${row.finished_width}'' x ${row.finished_height}''`;
    }
};

function filterConfigTupleAsMatcher([filter, config]: [Filter, FilterConfig]): (row: TemplatePair) => boolean
{
    if (!config) return () => true; // No config = no filtering

    if (filter.type === "basic" && config.type === "basic") {
        if (config.activeOptions.length === 0) return () => true;

        // Get all matchers from active options
        const activeMatchers = filter.options
            .filter(opt => config.activeOptions.includes(opt.label))
            .flatMap(opt => opt.matchers);

        // Row passes if ANY active option matches
        return (row: TemplatePair) =>
            activeMatchers.some(matcher => applyMatcher(matcher, row));
    }

    if (filter.type === "discreteRange" && config.type === "discreteRange") {
        // Get all matchers from options within range
        const rangeMatchers = filter.options
            .slice(config.indexMin ?? 0, (config.indexMax ?? Infinity) + 1)
            .flatMap(opt => opt.matchers);

        return (row: TemplatePair) =>
            rangeMatchers.some(matcher => applyMatcher(matcher, row));
    }

    console.warn(`Got weird filter or config types: [filter: ${filter.type}, config: ${config.type}]`);
    return () => true; // Fallback
}

function labelAsQueryValue(label: string)
{
    return `${label.replaceAll(/\W/g, "").toLowerCase()}`;
}

function applyMatcher(matcher: FilterOptionMatcher, row: TemplatePair): boolean {
    switch (matcher.type) {
        case "fieldValue": {
            const fieldValue = row[matcher.config.field];
            return fieldValue == matcher.config.value;
        }

        case "mappedValue": {
            const mapFn = MAPPED_VALUE_MATCHER_MAPS[matcher.config.mapName];
            if (!mapFn) {
                console.warn(`Unknown map: ${matcher.config.mapName}`);
                return false;
            }
            const mappedValue = mapFn(row);
            return mappedValue === matcher.config.expectedValue;
        }

        case "templateKeywords": {
            const keywords: string[] = row.template_keywords?.[0]?.split(',') ?? [];
            return matcher.config.some(keyword =>
                keywords.some(k => k.trim().toLowerCase().includes(keyword.toLowerCase()))
            );
        }
        
        case "variationIds": {
            const isVariationPair   = row.relation_type == "variation";
            const variationId       = isVariationPair ? row.id.split("_")[1] : null;
            return matcher.config.includes(variationId);
        }

        default:
            console.warn(`Unknown matcher type: ${(matcher as any).type}`);
            return false;
    }
}

function resultSetMap(filtersConfigsTuples: (readonly [Filter, FilterConfig])[])
{
    return (resultSet: TemplatePair[]) =>
    {
        const configuredFilterMatchers = filtersConfigsTuples.map(filterConfigTupleAsMatcher);
        return resultSet.filter(t => configuredFilterMatchers.every(matcher => matcher(t)));
    };
}

